import { config } from "@/config/env";
import { type VideoJob } from "@/types/video";

export interface GetVideosResponse {
  videos: VideoJob[];
  total: number;
}

export interface GetVideoStatsResponse {
  total: number;
  completed: number;
  processing: number;
  failed: number;
}

const API_BASE_URL = config.apiUrl;

async function fetchWithAuth(path: string, token: string, options: RequestInit = {}) {
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    ...options.headers,
  };
  
  const response = await fetch(`${API_BASE_URL}${path}`, { ...options, headers });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `API Error: ${response.status} ${response.statusText}`);
  }
  
  return response.json();
}

export const videosApi = {
  getVideos: async (token: string, page = 1, limit = 10, search = "", status = "all") => {
    const offset = (page - 1) * limit;
    let query = `?limit=${limit}&offset=${offset}`;
    if (search) query += `&search=${encodeURIComponent(search)}`;
    if (status && status !== "all") query += `&status=${status}`;
    
    // The backend returns { jobs: [], total: number } but we want { videos: [], total: number }
    const response = await fetchWithAuth(`/admin/videos${query}`, token);
    return {
        videos: response.jobs,
        total: response.total
    } as GetVideosResponse;
  },

  getStats: async (token: string) => {
    // Backend returns { totalJobs, byStatus: { completed: n, ... }, ... }
    const response = await fetchWithAuth(`/admin/videos/stats`, token);
    const byStatus = response.byStatus || {};
    
    return {
        total: response.totalJobs || 0,
        completed: byStatus.completed || 0,
        processing: (byStatus.processing || 0) + (byStatus.pending || 0), // Group pending with processing if desired, or just processing
        failed: byStatus.failed || 0
    } as GetVideoStatsResponse;
  },
};
